// Client-side image prep for photo uploads.
//
// Every photo is re-encoded client-side via a canvas round-trip before upload. This
// strips ALL metadata (EXIF, GPS, maker notes) on-device, before the file ever leaves
// the browser — the server also strips metadata by re-encoding (belt-and-suspenders),
// but that happens after the bytes have already reached the server process. Stripping
// client-side means GPS/EXIF never leaves the user's device at all.
//
// HEIC handling: iPhones (Safari) capture image/heic by default. The server does NOT
// decode HEIC (pure-Go, no libheif). The same canvas transcode that strips metadata also
// converts HEIC -> JPEG on-device, so the server only ever receives JPEG/PNG. Safari
// natively decodes HEIC, which is the only browser that emits it, so canvas suffices —
// no WASM decoder needed.
//
// Output format: JPEG sources re-encode as JPEG; PNG sources re-encode as PNG (preserves
// transparency — forcing JPEG would flatten alpha onto an opaque background). HEIC always
// becomes JPEG, since there's no transparency to preserve and it shrinks the upload.

const MAX_UPLOAD_BYTES = 15 * 1024 * 1024; // matches server cap

// isHeic detects HEIC/HEIF by MIME or extension (some browsers report an empty type).
function isHeic(file) {
  const t = (file.type || "").toLowerCase();
  if (t.includes("heic") || t.includes("heif")) return true;
  return /\.(heic|heif)$/i.test(file.name || "");
}

// isPng detects PNG by MIME or extension, to preserve transparency on re-encode.
function isPng(file) {
  const t = (file.type || "").toLowerCase();
  if (t.includes("png")) return true;
  return /\.png$/i.test(file.name || "");
}

// transcodeViaCanvas draws a File to a canvas and re-encodes it, which strips all
// metadata (EXIF/GPS/maker notes) as a side effect of the decode->redraw->re-encode.
// Throws on decode failure (e.g. HEIC in a non-Safari browser with no native decoder).
function transcodeViaCanvas(file, { mimeType, extension, quality }) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("could not encode image"));
            return;
          }
          const name =
            (file.name || "photo").replace(/\.(heic|heif|png|jpe?g)$/i, "") + extension;
          resolve(new File([blob], name, { type: mimeType }));
        },
        mimeType,
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("could not decode image in this browser"));
    };
    img.src = url;
  });
}

// prepareForUpload returns a File ready to POST: always re-encoded client-side to strip
// metadata before it leaves the device. HEIC -> JPEG; PNG stays PNG (keeps transparency);
// everything else re-encodes as JPEG. Throws with a user-facing message on unsupported
// HEIC or oversize.
export async function prepareForUpload(file) {
  const png = isPng(file);
  const target = png
    ? { mimeType: "image/png", extension: ".png", quality: undefined }
    : { mimeType: "image/jpeg", extension: ".jpg", quality: 0.9 };

  let out;
  try {
    out = await transcodeViaCanvas(file, target);
  } catch {
    if (isHeic(file)) {
      throw new Error(
        "This looks like a HEIC photo your browser can't convert. Please upload a JPEG or PNG."
      );
    }
    throw new Error("Could not process this photo. Please try a different image.");
  }

  if (out.size > MAX_UPLOAD_BYTES) {
    throw new Error("Photo is larger than 15 MB. Please choose a smaller image.");
  }
  return out;
}

// isTouchDevice picks the file <input accept>: mobile gets the camera + HEIC (which we
// transcode); desktop is restricted to JPEG/PNG so unconvertible HEIC can't be selected.
export function isTouchDevice() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(pointer: coarse)").matches ?? "ontouchstart" in window;
}
